import uuid from 'uuid/v4'
import { configureScope, captureException } from '@sentry/browser'
import { uniq } from 'lodash'
import AppConfig from '../../../../src/app-config'

export const tokenKey = (userId, role) => `user:${userId}:role:${role}`

export function parseJwt(token) {
  const base64Url = token.split('.')[1]
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
      .join('')
  )

  return JSON.parse(jsonPayload)
}

/**
 * Sets userId, districtId, role, kid, tid to Sentry for
 * authenticated users, and sets kid & tid for
 * unauthenticated users
 */
export const updateSentryScope = (user) => {
  if (AppConfig.sentryURI) {
    // for authenticated users user will be passed
    if (user && user._id) {
      const { _id, role, districtId, kid } = user
      configureScope((scope) => {
        scope.setUser({ id: _id })
        scope.setTags({
          tid: window.sessionStorage.tid,
          districtId,
          role,
          kid,
        })
      })
    } else if (window.sessionStorage.kid) {
      // In case user is unauthenticated then set kid & tid to Sentry tags
      configureScope((scope) => {
        scope.setTags({
          tid: window.sessionStorage.tid,
          kid: window.sessionStorage.kid,
        })
      })
    }
  }
}

export function storeAccessToken(token, userId, role, _default = false) {
  const key = tokenKey(userId, role)
  window.localStorage.setItem(key, token)
  const tokens = JSON.parse(window.localStorage.getItem('tokens') || '[]')

  window.localStorage.setItem(
    'tokens',
    JSON.stringify([...new Set([...tokens, key])])
  )
  if (_default) {
    window.sessionStorage.defaultTokenKey = key
  }
}

export function selectAccessToken(userId, role) {
  window.sessionStorage.tokenKey = tokenKey(userId, role)
}

/**
 * Update kid after user authentication
 * and sets the same to sentry scope
 * @param {*} user
 */
export function updateKID({ _id, role, districtId, kid }) {
  if (window.sessionStorage) {
    window.sessionStorage.kid = kid
    updateSentryScope({ _id, role, districtId, kid })
  }
}

export function updateUserToken(token, kid) {
  try {
    const { _id: userId, role, districtId } = parseJwt(token)
    storeAccessToken(token, userId, role, true)
    selectAccessToken(userId, role) // in case role changed
    if (kid) {
      updateKID({ _id: userId, role, kid, districtId })
    }
  } catch (e) {
    captureException(e)
  }
}

export function removeAccessToken(userId, role) {
  const key = tokenKey(userId, role)
  const oldTokens = JSON.parse(window.localStorage.getItem('tokens') || '[]')
  window.localStorage.setItem(
    'tokens',
    JSON.stringify(oldTokens.filter((x) => x != key))
  )
  window.localStorage.removeItem(key)
}

export function getAccessTokenForUser(userId, role) {
  const key = tokenKey(userId, role)
  const oldTokens = JSON.parse(window.localStorage.getItem('tokens') || '[]')
  return oldTokens.find((x) => x === key)
}

/** JWT for userId when session was opened via proxy (has proxyUid). */
export function getProxiedAccessTokenForUser(userId) {
  const tokens = JSON.parse(window.localStorage.getItem('tokens') || '[]')
  const userPrefix = `user:${userId}:`
  for (const key of tokens) {
    if (!key.startsWith(userPrefix)) {
      continue
    }
    const token = window.localStorage.getItem(key)
    if (!token) {
      continue
    }
    try {
      if (parseJwt(token).proxyUid) {
        return token
      }
    } catch (e) {
      captureException(e)
    }
  }
}

/**
 * sessionStorage key holding an account-link intent token across an SSO round trip.
 * Kept next to `addAccountDetails`, which is scoped the same way and lives the same long.
 */
const ACCOUNT_LINK_INTENT_KEY = 'accountLinkIntent'

/**
 * Parks an account-link intent token so it survives the redirect out to an external IdP.
 *
 * Every SSO sign-in calls `removeAllTokens()` on its way out of the page, which destroys
 * the only proof this browser owns the source account. The intent therefore has to be
 * minted *before* the redirect, while that proof still exists, and read back on the
 * callback — see `prepareSsoLinkIntent` in the login ducks.
 *
 * sessionStorage rather than localStorage: the value belongs to the add-account tab, dies
 * with it, and is never visible to the source account's own tab. It is also narrower than
 * what it replaces — single-use, 15 minutes, and it authorises nothing but this one link.
 */
export function stashAccountLinkIntent(userId, token) {
  if (!userId || !token) {
    return
  }
  window.sessionStorage.setItem(
    ACCOUNT_LINK_INTENT_KEY,
    JSON.stringify({ userId: String(userId), token })
  )
}

/**
 * Reads back a parked intent token and clears it, so a token is offered at most once.
 *
 * Returns undefined unless the parked token was minted for `userId`. Without that check a
 * leftover intent for one account could be spent on a link into another, which is the
 * class of confusion this whole handshake exists to prevent.
 */
export function takeAccountLinkIntent(userId) {
  if (!userId) {
    return undefined
  }
  const raw = window.sessionStorage.getItem(ACCOUNT_LINK_INTENT_KEY)
  if (!raw) {
    return undefined
  }
  window.sessionStorage.removeItem(ACCOUNT_LINK_INTENT_KEY)
  try {
    const { userId: stashedUserId, token } = JSON.parse(raw)
    return stashedUserId === String(userId) ? token : undefined
  } catch (e) {
    captureException(e)
    return undefined
  }
}

export function clearAccountLinkIntent() {
  window.sessionStorage.removeItem(ACCOUNT_LINK_INTENT_KEY)
}

export function removeTokens() {
  window.localStorage.removeItem('tokens')
}

// Initialise browser tab id
// Will persist till the tab is closed
export const initTID = () => {
  if (
    window.sessionStorage != null &&
    window.sessionStorage.tid !== '' &&
    !window.sessionStorage.tid
  ) {
    window.sessionStorage.tid = uuid.v4()
  }
}

// Initialise kid for unauthenticated user
export const initKID = () => {
  if (
    window.sessionStorage != null &&
    window.sessionStorage.kid !== '' &&
    !window.sessionStorage.kid
  ) {
    window.sessionStorage.kid = uuid.v4()
    updateSentryScope()
  }
}

// Remove kid after user logout
export const removeKID = () => {
  if (window.sessionStorage) {
    delete window.sessionStorage.kid
  }
}

// function to fetch values from sessionStorage
export const getFromSessionStorage = (key) => {
  if (window && window.sessionStorage) {
    return window.sessionStorage.getItem(key)
  }
}

// Trace id -- a way to connect client requests(on ALB) to consumer(lambda)
export const getTraceId = () =>
  `tid=${window.sessionStorage.tid};kid=${window.sessionStorage.kid}`

export function getAccessToken() {
  let _tokenKey = window.sessionStorage.tokenKey
  if (!_tokenKey) {
    _tokenKey = window.sessionStorage.defaultTokenKey
  }
  if (!_tokenKey) {
    // to keep backward compatible
    _tokenKey = window.localStorage.defaultTokenKey
  }
  if (!_tokenKey) {
    const tokens = window.localStorage.getItem('tokens')
    if (tokens) {
      _tokenKey = JSON.parse(tokens)[0]
    }
  }
  return window.localStorage.getItem(_tokenKey)
}

/**
 * The user id whose session the API client will authenticate as, i.e. whoever
 * `getAccessToken` currently resolves to.
 *
 * Add-account needs this to confirm the tab really holds the account it claims to be
 * adding to. Normally it does — the add-account tab inherits the opener's sessionStorage
 * `tokenKey` — but a pasted add-account URL in a fresh tab falls through to `tokens[0]`,
 * which in a profile with several stored accounts can be an entirely different user.
 * Minting there would link the wrong account, silently.
 */
export function getCurrentSessionUserId() {
  const token = getAccessToken()
  if (!token) {
    return undefined
  }
  try {
    const { _id } = parseJwt(token)
    return _id ? String(_id) : undefined
  } catch (e) {
    captureException(e)
    return undefined
  }
}

export function getCurrentTokenCreatedTime() {
  const token = getAccessToken()
  const { tokenCreatedTime } = parseJwt(token)
  return tokenCreatedTime
}

export const SHOW_JOYRIDE_IN_PROXY_KEY = 'showJoyrideInProxy'

/**
 * Disable joyrides while proxying another user, unless the admin explicitly opted
 * to show them for this proxied tab (set via the proxy form checkbox). Reads the
 * `proxyUid` claim off the current token (the same signal `isProxy` is derived
 * from), so it works without Redux and can be called from shared packages.
 */
export function isJoyrideDisabledForProxy() {
  try {
    const showJoyrideInProxy = window.sessionStorage.getItem(
      SHOW_JOYRIDE_IN_PROXY_KEY
    )
    return (
      !!parseJwt(getAccessToken()).proxyUid && showJoyrideInProxy !== 'true'
    )
  } catch (e) {
    return false
  }
}

/**
 * Removes all user tokens in current browser
 */
export function removeAllTokens() {
  window.sessionStorage.removeItem('tokenKey')
  window.sessionStorage.removeItem('defaultTokenKey')
  window.localStorage.removeItem('defaultTokenKey')
  window.localStorage.removeItem('tokens')
}

export function storeInLocalStorage(key, value) {
  if (window && window.localStorage) {
    window.localStorage.setItem(key, value)
  }
}

export function getFromLocalStorage(key) {
  if (window && window.localStorage) {
    return window.localStorage.getItem(key)
  }
}

export function removeFromLocalStorage(key) {
  if (window && window.localStorage) {
    return window.localStorage.removeItem(key)
  }
}

export function getTokens() {
  return JSON.parse(getFromLocalStorage('tokens')) || []
}
// get proxy parent
// proxy parent is the user who initiated the proxy of another user
export function getProxyParent(roles = []) {
  try {
    const proxyParent = JSON.parse(getFromLocalStorage('proxyParent'))
    if (
      (!roles.length || roles.includes(proxyParent.role)) &&
      proxyParent.role
    ) {
      return proxyParent
    }
    return null
  } catch (e) {
    return null
  }
}

export function addPlaylistIdToDeleted(id) {
  if (window && window.sessionStorage) {
    const { deletedPlaylists = '[]' } = window.sessionStorage
    window.sessionStorage.deletedPlaylists = JSON.stringify(
      uniq([...JSON.parse(deletedPlaylists), id])
    )
  }
}

export function getDeletedPlaylistIds() {
  return JSON.parse(getFromSessionStorage('deletedPlaylists') || '[]')
}

export function tokenExpireInHours() {
  const currentTime = new Date().getTime()
  const token = getAccessToken()
  const tokenParsed = parseJwt(token)
  const timeDiff = (tokenParsed.exp * 1000 - currentTime) / 3600000
  return timeDiff
}

/**
 * Stores class and selected student IDs in local storage for generated custom content
 * from standards based report in LCB
 * @param {Object} metadata - The metadata object containing student and test information
 * @param {string[]} metadata.selectedStudentsIds - Array of selected student IDs
 * @param {string} metadata.classId - ID of the class
 * @param {string} metadata.testId - ID of the test
 * @param {string} metadata.userId - ID of the author/user
 * @param {string} metadata.districtId - ID of the district
 */
export function storeClassStudentAssignmentMetadata(metadata = {}) {
  const {
    selectedStudentsIds = [],
    classId = '',
    testId = '',
    userId: authorId = '',
    districtId = '',
  } = metadata || {}

  if (
    selectedStudentsIds?.length &&
    classId &&
    testId &&
    authorId &&
    districtId
  ) {
    const key = `standardMasteryCustomContent:${districtId}:${authorId}:${testId}`
    const value = `${classId}:${selectedStudentsIds.join(',')}`
    storeInLocalStorage(key, value)
  }
}
